"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CircleCheck } from "lucide-react";
const timelineSteps = [{
  title: "EU-gemensamma riktlinjer",
  description: "Första steget är att etablera tydliga EU-direktiv för typgodkännande av helt autonoma fordon (nivå 4 och 5) utan traditionella förarreglage. Detta skapar en enhetlig standard för säkerhet och teknik över hela Europa."
}, {
  title: "Svensk lagstiftning uppdateras",
  description: "Transportstyrelsen och regeringen måste anpassa den nationella trafikförordningen. Detta innebär att juridiskt definiera ansvarsfrågan när en AI kör fordonet och tillåta kommersiell drift av förarlösa fordon på allmän väg."
}, {
  title: "Infrastruktur och uppkoppling",
  description: "Svenska städer behöver säkerställa robust 5G-täckning och digital infrastruktur. Fordonen måste kunna kommunicera i realtid med varandra (V2V) och med stadens infrastruktur (V2I) för maximal säkerhet."
}, {
  title: "Lokala tillstånd och tester",
  description: "Slutligen krävs godkännande från enskilda kommuner för att etablera zoner där robotaxi-operatörer kan bedriva sin verksamhet. Detta föregås ofta av omfattande pilottester i avgränsade stadsmiljöer."
}];
function TimelineItem({
  step,
  index
}: {
  step: (typeof timelineSteps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative flex flex-col md:flex-row w-full mb-12 last:mb-0 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute left-[23px] md:left-1/2 top-6 size-5 rounded-full bg-primary ring-4 ring-secondary -translate-x-1/2 z-10 flex items-center justify-center">
        <div className="size-2 rounded-full bg-secondary" />
      </div>

      {}
      <div className={`w-full md:w-[calc(50%-3rem)] pl-16 md:pl-0 ${isEven ? "md:pr-12 md:text-right" : "md:pl-12 md:ml-auto"}`}>
        <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
          <CardHeader className="pb-3">
            <div className={`flex items-center gap-3 mb-2 ${isEven ? "md:justify-end" : "justify-start"}`}>
              <span className="flex items-center justify-center size-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                {index + 1}
              </span>
              <CardTitle className="font-serif text-xl font-bold text-foreground">
                {step.title}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="font-sans text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function PathToSwedishApproval() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="vägen-till-svenskt-godkännande" className="bg-secondary py-24 px-6 overflow-hidden">
      <div className="max-w-5xl mx-auto">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
            Vägen till svenskt godkännande
          </h2>
          <p className="font-sans text-muted-foreground max-w-2xl mx-auto text-lg">
            För att robotaxi ska bli verklighet på svenska vägar krävs flera
            viktiga steg, från internationella regelverk till lokala
            anpassningar och teknisk infrastruktur.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {}
          <div className="absolute left-[23px] md:left-1/2 top-6 bottom-6 w-0.5 bg-primary/20 -translate-x-1/2" />

          {}
          <div className="relative z-10">
            {timelineSteps.map((step, index) => <TimelineItem key={index} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}